/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';

import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { PADDINGS, COLORS2_AS_BACKGROUND } from '^/constants/commonStyles';
import { ReservationDetailed } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';

import { HoverToReveal } from '../../../../commonComponents/ui/HoverToReveal';
import { AdditionalFees2 } from '../../AdditionalFees2';
import { PPDOnsitePaymentHoverMessage } from '../../onsiteInfoMessages';

import {
  CancelButtonStyle,
  billDivider,
  billLabel,
  billRow,
  billSubValue,
  billTitle,
  billTotalLabel,
  billTotalValue,
  billValue,
  flexRightmostItemRow,
} from './ReservationDetailDesktop.commonStuff';
import { CancellationWarningBeforePickup72Hours } from '../../CancellationWarningBeforePickup72hours';
import { formatDateString } from '^/util/dateFormat';
import { ExtraChargeDisplayCurrency } from '../../extraChargeDisplay';

function isBookedAndPPDReservation(data: ReservationDetailed) {
  if (data.res_status !== 'booked' || data.pay_type !== 'PPD') {
    throw new Error('DummyErrorForTypeFiltering');
  }
  return data;
}

export const ReservationDetailsBillPPDBooked: React.FC<{
  data: ReturnType<typeof isBookedAndPPDReservation>;
  onClickCancelReservation(reservationToCancel: ReservationDetailed): void;
  extraChargeDisplayCurrency?: ExtraChargeDisplayCurrency;
}> = observer(function ReservationDetailsBillPPDBooked({
  data,
  onClickCancelReservation,
  extraChargeDisplayCurrency = 'foreign',
}) {
  const { t } = useTranslation();

  const paymentTime = formatDateString(data.date_request, 'YYYY.MM.DD HH:mm (z)', 'Asia/Seoul');
  const isKrwDisplay = extraChargeDisplayCurrency === 'krw';
  const onSiteAmount = isKrwDisplay
    ? data.extra_charge?.additional_rate_amount ?? data.extra_charge?.additional_amount
    : data.extra_charge?.additional_amount;
  const onSiteCurrencyLabel = isKrwDisplay ? t('won') : data.paymentDetails.onSiteCurrency;
  const formatForeignAmount = (value?: number | string | null) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div className="w-full">
      <h3 className={billTitle}>{t('total')}</h3>

      <div
        id="separator1"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />

      <div className={['flex flex-col gap-[10px]'].join(' ')}>
        <div className={billRow}>
          <p className={billLabel}>{t('fee')}</p>
          <p className={billValue}>{formatKRWWonAmount(data.rate_amount)} 원</p>
        </div>
        {data.extra_charge && (data.extra_charge.additional_amount || 0) > 0 && (
          <div className={['mt-[5px]'].join(' ')}>
            <AdditionalFees2
              extra_charge={data.extra_charge}
              onSiteCurrency={data.extra_equipments_json.currency}
              displayCurrency={extraChargeDisplayCurrency}
            />
          </div>
        )}

        <div
          id="separator"
          className={[
            billDivider,
            css`
              margin-top: 12px;
              margin-bottom: 12px;
            `,
          ].join(' ')}
        />
        <div className={billRow}>
          <span className={billTotalLabel}>즉시 결제 금액</span>

          <span className={billTotalValue}>{formatKRWWonAmount(data.paid_amount)} 원</span>
        </div>
        {!isKrwDisplay && !!data.extra_charge?.additional_amount && (
          <>
            <div
              className={[
                billRow,
                css`
                  position: relative;
                `,
              ].join(' ')}
            >
              <div
                className={[
                  billLabel,
                  css`
                    display: flex;
                    align-items: center;
                    justify-items: center;
                    height: fit-content;
                    gap: 6px;
                  `,
                ].join(' ')}
              >
                <span>현장 결제 예상금액</span>
                <HoverToReveal
                  toReveal={
                    <div
                      className={[
                        billSubValue,
                        PADDINGS.TOP_AND_BOTTOM.spacing16,
                        COLORS2_AS_BACKGROUND.sub.MrtGray3,
                        css`
                          position: absolute;
                          left: 0;
                          right: 0;
                          top: 100%;
                          padding-left: 10px;
                          padding-right: 10px;
                          border-radius: 10px;
                          text-align: left;
                        `,
                      ].join(' ')}
                    >
                      {PPDOnsitePaymentHoverMessage}
                    </div>
                  }
                />
              </div>

              <p className={billValue}>
                {isKrwDisplay
                  ? formatKRWWonAmount(Number(onSiteAmount), 0.01)
                  : formatForeignAmount(onSiteAmount)}
                &nbsp;
                {onSiteCurrencyLabel}
              </p>
            </div>
            <p className={[billSubValue, flexRightmostItemRow].join(' ')}>
              약 &nbsp;
              {formatKRWWonAmount(Number(data.extra_charge.additional_rate_amount), 0.01)}
              &nbsp;
              {t('won')}
            </p>
          </>
        )}
      </div>

      <div
        id="separator2"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />
      <div className={billRow}>
        <p className={billLabel}>결제 수단</p>
        <p className={billValue}>신용카드</p>
      </div>
      <div className={[billRow, 'mt-[10px]'].join(' ')}>
        <p className={billLabel}>결제 일시</p>
        <p className={billValue}>{paymentTime}</p>
      </div>
      <button
        type="button"
        onClick={() => onClickCancelReservation(data)}
        disabled={data.cancelDisabled}
        className={CancelButtonStyle}
      >
        예약 취소하기
      </button>
      <CancellationWarningBeforePickup72Hours show={data.show72HoursCancelWarning} />
    </div>
  );
});
